import Container from '@/components/Container';

import ResourceHubSkeleton from './ResourceHub.skeleton';
import { useNavigate, useParams } from 'react-router-dom';
import useIsTablet from '@/hooks/useIsTablet';
import { useQueryCacheAndNetwork } from '@/hooks/useQueryCacheAndNetwork';
import {
  LatestResourcesByTypeQuery,
  LatestResourcesByTypeQueryVariables,
  QUERY_FETCH_LATEST_RESOURCES_BY_TYPEE,
  QUERY_FETCH_RESOURCE_HUB_PAGE,
  Resource,
  ResourceHubPageQuery,
  ResourceHubPageQueryVariables,
  ResourceType,
} from '@edwin-group/cj-graphql';
import PageHeader from '@/components/PageHeader';
import AnimatedFadeInComponent from '@/components/AnimatedFadeInComponent';
import ResourceCard from '@/components/Card/Resource';
import Divider from '@/components/Divider';
import { useEffect, useMemo, useState } from 'react';
import ResourceTypesFilter from './ResourceTypesFilter';
import { compare } from './utils/compare.utils';
import Button from '@/components/Button';
import { fetchAwarenessDaysResources } from '@/services/resource-hub/resource-hub.service';
import { getAuthentication } from '@/hooks/useAuthentication';

function renderCards({
  title,
  data,
  viewAll = false,
  showDivider = true,
  viewAllText = '+ More',
  handleLoadMore,
}: {
  title: string;
  data: Resource[];
  showDivider?: boolean;
  viewAll?: boolean;
  viewAllText?: string;
  handleLoadMore?: () => void;
}) {
  if (!data?.length) {
    return null;
  }

  return (
    <AnimatedFadeInComponent key={title}>
      <div key={title} className="mb-sh-30">
        <div className="flex items-center justify-between">
          <h3 className="mb-sh-30 font-cadiz text-sh-md font-bold">{title}</h3>
        </div>
        <div className="grid grid-cols-1 gap-sh-30 md:grid-cols-2 md:gap-4">
          {data?.map((resource: Resource) => {
            return <ResourceCard resource={resource} key={resource?.attributes?.slug} />;
          })}
        </div>
        {viewAll && handleLoadMore && (
          <div className="mt-sh-30 flex w-full items-center justify-end">
            <Button theme="outline" onClick={handleLoadMore}>
              {viewAllText}
            </Button>
          </div>
        )}
      </div>
      {showDivider ? <Divider /> : null}
    </AnimatedFadeInComponent>
  );
}

export default function ResourceHub() {
  const { type: typeParam } = useParams();
  const [awarenessCalendarResources, setAwarenessDaysResources] = useState<Resource[]>([]);
  const [latestResourcePage, setLatestResourcePage] = useState(1);
  const [totalLatestPages, setTotalLatestPages] = useState(1);
  const navigate = useNavigate();

  const [latestResources, setLatestResources] = useState<Resource[]>([]);

  const { data: resourceHubPage, loading: loadingPage } = useQueryCacheAndNetwork<
    ResourceHubPageQuery,
    ResourceHubPageQueryVariables
  >(QUERY_FETCH_RESOURCE_HUB_PAGE);

  useEffect(() => {
    const fetchAllCalendarResources = async () => {
      const data = await fetchAwarenessDaysResources();
      setAwarenessDaysResources((data?.resources as Resource[]) || []);
    };

    fetchAllCalendarResources();
  }, []);

  useEffect(() => {
    setLatestResourcePage(1);
    setTotalLatestPages(1);
    setLatestResources([]);
  }, [typeParam]);

  useEffect(() => {
    if (typeParam) {
      return;
    }
    const contentTypes = resourceHubPage?.resourceTypes?.map((rt) => rt?.type).filter((t): t is string => !!t) ?? [];

    if (contentTypes.length === 0) {
      return;
    }

    navigate(`/resource-hub/type/${contentTypes[0]}`);
  }, [resourceHubPage?.resourceTypes, typeParam, navigate]);

  const contentTypes = useMemo(() => {
    return resourceHubPage?.resourceTypes?.map((rt) => rt?.type).filter((t): t is string => !!t) ?? [];
  }, [resourceHubPage?.resourceTypes]);

  const currentContentType =
    (resourceHubPage?.resourceTypes?.find((contentType) => compare(contentType?.type, typeParam)) as ResourceType) ||
    {};

  const { fetchMore } = useQueryCacheAndNetwork<LatestResourcesByTypeQuery, LatestResourcesByTypeQueryVariables>(
    QUERY_FETCH_LATEST_RESOURCES_BY_TYPEE,
    {
      variables: {
        types:
          typeParam && contentTypes?.some((contentType) => compare(contentType, typeParam))
            ? [typeParam]
            : contentTypes,
        limit: 2,
        page: 1,
        staffId: getAuthentication()?.user?.documentId || '',
      },
      notifyOnNetworkStatusChange: true,
      onCompleted: (data) => {
        if (!data?.resources?.data) {
          return;
        }

        setTotalLatestPages(data?.resources?.meta?.pagination?.pageCount);
        setLatestResources(data?.resources?.data as unknown as Resource[]);
      },
    },
  );

  const handleLoadMore = async () => {
    const nextPage = latestResourcePage + 1;
    const { data: newData } = await fetchMore({
      variables: {
        types:
          typeParam && contentTypes?.some((contentType) => compare(contentType, typeParam))
            ? [typeParam]
            : contentTypes,
        limit: 2,
        page: nextPage,
      },
    });

    if (!newData?.resources?.data) {
      return;
    }

    setTotalLatestPages(newData?.resources?.meta?.pagination?.pageCount);
    setLatestResources((prev) => [...prev, ...((newData?.resources?.data as unknown as Resource[]) || [])]);
    setLatestResourcePage(nextPage);
  };

  const { isTablet } = useIsTablet();
  if (loadingPage) {
    return <ResourceHubSkeleton />;
  }

  if (!typeParam) {
    return null;
  }

  return (
    <Container className="min-h-40" addMargin={!isTablet}>
      <div className="col-span-12 lg:col-start-3 lg:col-end-11">
        <div className="mb-sh-30">
          <PageHeader title="Resource Hub" url="/learn" type="none" />
        </div>
        <ResourceTypesFilter resourceTypes={contentTypes} />
        <h1 className="mb-sh-15 font-cadiz text-sh-md font-bold">
          {typeParam !== 'All' && typeParam ? typeParam : 'Resource hub'}
        </h1>
        <p className="mb-sh-60 font-cadiz text-sh-16-24">
          {currentContentType?.description || resourceHubPage?.resourceHubPage?.data?.attributes?.defaultDescription}
        </p>

        {renderCards({
          title: `${resourceHubPage?.resourceHubPage?.data?.attributes?.latestTitle || 'Latest'}` || '',
          data: latestResources,
          showDivider: false,
          viewAll: totalLatestPages > latestResourcePage,
          viewAllText: resourceHubPage?.resourceHubPage?.data?.attributes?.moreButtonTitle,
          handleLoadMore,
        })}

        {renderCards({
          title:
            `${resourceHubPage?.resourceHubPage?.data?.attributes?.awarenessResourcesTitle || 'Awareness Calendar'}` ||
            '',
          data: awarenessCalendarResources,
          showDivider: false,
        })}
      </div>
    </Container>
  );
}
